import { observer } from "mobx-react-lite";
import { t } from "i18next";
import type { NodeProps } from "@xyflow/react";
import { AddTriggerButton } from "../../AddTriggerButton";
import { AddedEventButton } from "../../AddedEventButton";
import { useRootStore } from "../../../stores/hooks";
import { useFlowCanvasHandlers } from "./context";
import { HiddenHandles } from "./handles";
import { NodeSectionLabel } from "./NodeSectionLabel";
import type { FlowNodeData } from "../graph/types";
import { SECTION_LABEL_HEIGHT } from "../graph/constants";
import * as T from "../../../stores/automation/mergers/trigger";
import * as A from "../../../stores/automation/mergers/actions";
import {
  candidateTriggerConditionsValid,
  candidateTriggerInputValid,
  triggersIdsAvailableForMergeRecordsAction,
} from "../../../stores/automation/utils/inputValidation";
import { pickValueDataString } from "../../../utils/inputs";
import { WHEN_BUTTON_IS_CLICKED_TRIGGER_ID } from "../../../utils/constants";

/**
 * React Flow wrapper over the legacy `AddedEventButton` trigger card. Error,
 * field-missing, disabled and the change-trigger confirm flow are copied
 * verbatim from `Sidebar/index.tsx`'s filled-trigger render (it reads store
 * validation state and calls the trigger/action mergers).
 */
export const TriggerNode = observer<NodeProps>(({ data }) => {
  const { sectionLabel } = data as FlowNodeData;
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const candidate = store.automations.candidate;
  const { trigger } = candidate.self;
  const actions = candidate.allActions;
  const actionGroupType =
    candidate.currentActionGroup?.config.oneofKind ?? "actions";

  const isActive = handlers.activeItem?.type === "trigger";

  const showTriggerError = (): boolean =>
    candidate.isTriggerSubmitted && !candidate.isTriggerValid();

  const showTriggerFieldMissingError = (): boolean => {
    if (!store.automations.isAllDynamicDescriptionsLoaded()) {
      return false;
    }
    return (
      !candidateTriggerInputValid(candidate.self) ||
      !candidateTriggerConditionsValid(candidate.self)
    );
  };

  const refreshDefaultValue = (triggerId: string): void => {
    if (triggerId === "schedule") {
      const sendEmail = actions.find(
        (action) => action.actionReference?.actionId === "send-email",
      );
      if (sendEmail != null) {
        const inAppMessage = sendEmail.inputs.find(
          (input) => input.inputId === "in-app-message-input",
        );
        if (inAppMessage?.value != null) {
          const value = pickValueDataString(inAppMessage.value);

          if (value === undefined) {
            inAppMessage.value = {
              values: [
                {
                  value: {
                    oneofKind: "string",
                    string: t(
                      "in-app-message-default-message.matches-condition",
                    ),
                  },
                },
              ],
            };
            candidate.setActions(
              [A.mergeInputs(sendEmail?.hash, sendEmail.inputs)],
              actionGroupType,
            );
          }
        }
      }
    }
  };

  const checkActionsToMergeRecordsForTrigger = (triggerId: string): void => {
    actions.forEach((action) => {
      if (
        action.actionReference?.actionId === "merge-records" &&
        !triggersIdsAvailableForMergeRecordsAction.includes(triggerId ?? "")
      ) {
        candidate.setActions(
          [A.mergeActionTriggerAllowedError(action.hash, true)],
          "actions",
        );
      } else {
        candidate.setActions(
          [A.mergeActionTriggerAllowedError(action.hash, false)],
          "actions",
        );
      }
    });
  };

  const triggerId = trigger.triggerReference?.triggerId ?? "";

  if (triggerId === "") {
    return (
      <>
        <HiddenHandles
          targetTopOffset={
            sectionLabel !== undefined ? SECTION_LABEL_HEIGHT : undefined
          }
        />
        <NodeSectionLabel label={sectionLabel} />
        <AddTriggerButton
          label={t("general.add-trigger")}
          showError={showTriggerError()}
          onClick={(integrationId, selectedTriggerId) => {
            candidate.setTrigger(
              T.mergeReferenceIds(integrationId, selectedTriggerId),
            );
            candidate.setIsTriggerSubmitted(false);
            handlers.onClickTrigger();
          }}
        />
      </>
    );
  }

  const preview = store.flowCanvas.loopSourcePreviewTarget;
  const isLoopSourcePreview = preview?.kind === "trigger";

  return (
    <>
      <HiddenHandles />
      <NodeSectionLabel label={sectionLabel} />
      <div data-loop-source-preview={String(isLoopSourcePreview)}>
        <AddedEventButton
          groupType="actions"
          eventType="trigger"
          trigger={trigger}
          isActive={isActive}
          isHighlighted={isActive}
          error={showTriggerError()}
          fieldMissingError={showTriggerFieldMissingError()}
          disabled={
            (trigger.triggerReference?.triggerId ===
              WHEN_BUTTON_IS_CLICKED_TRIGGER_ID &&
              candidate.self.automationId !== "") ||
            (trigger.triggerDisabled ?? false) ||
            (trigger.integrationDisabled ?? false)
          }
          onClick={handlers.onClickTrigger}
          onChange={(integrationId, triggerId) => {
            store.modal.openConfirmModal({
              type: "warning",
              title: t("confirm-modal.are-you-sure"),
              description: `${t("confirm-modal.want-change-trigger")} ${t(
                "confirm-modal.this-may-invalidate-trigger",
              )}. ${t("confirm-modal.cannot-undone-action")}.`,
              positiveButtonText: t("confirm-modal.yes-change-trigger"),
              negativeButtonText: t("confirm-modal.no-cancel"),
              onClose: () => {
                store.modal.closeConfirmModal();
              },
              onConfirm: () => {
                candidate.setTrigger(
                  T.mergeReferenceIds(integrationId, triggerId),
                );
                candidate.setActionsWithOutReferences();
                candidate.looping.clearSourceIfTriggerReference();
                candidate.branches.clearConditionsIfTriggerReference();
                store.modal.closeConfirmModal();
                handlers.onClickTrigger();
                // this is a function to avoid validation errors in the hidden field
                refreshDefaultValue(triggerId);
                checkActionsToMergeRecordsForTrigger(triggerId);
              },
            });
          }}
        />
      </div>
    </>
  );
});
TriggerNode.displayName = "TriggerNode";
